import { makeAutoObservable, runInAction } from "mobx";
import { db } from "@stores/environments/Database.web";
import { ref, get, set, push, child } from "firebase/database";
import { IContent } from "@interfaces/icontent";
import { logService } from "../services/LogService";

class ActivityStore {
  activities: IContent[] = [];
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchActivities = async () => {
    this.loading = true;
    const dbRef = ref(db);

    try {
      const snapshot = await get(child(dbRef, "atividades"));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const allActivities: IContent[] = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        runInAction(() => {
          this.activities = allActivities
            .filter(activity => !activity.deleted)
            .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.activities = [];
          this.loading = false;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar atividades:", error);
      runInAction(() => {
        this.error = "Erro ao carregar atividades.";
        this.loading = false;
      });
    }
  };

  saveActivity = async (activity: Omit<IContent, "id">, id?: string) => {
    const activityId = id || push(ref(db, "atividades")).key;
    if (!activityId) {
      console.error('Erro ao gerar ID para atividade');
      return;
    }
    const dbRef = ref(db, `atividades/${activityId}`);
    
    // Verifica se é uma criação ou atualização
    const isUpdate = !!id;
    const action = isUpdate ? 'UPDATE' : 'CREATE';
    
    // Busca o valor anterior se for uma atualização
    let oldValue = null;
    if (isUpdate) {
      try {
        const snapshot = await get(dbRef);
        oldValue = snapshot.exists() ? snapshot.val() : null;
      } catch (error) {
        console.error('Erro ao buscar valor anterior:', error);
      }
    }
    
    await set(dbRef, activity);
    
    // Registra o log
    const logData = isUpdate 
      ? logService.updateLogData(
          'Activity',
          activityId,
          `atividades/${activityId}`,
          oldValue,
          activity,
          `Atividade ${isUpdate ? 'atualizada' : 'criada'}: ${activity.title}`
        )
      : logService.createLogData(
          'Activity',
          activityId,
          `atividades/${activityId}`,
          activity,
          `Atividade criada: ${activity.title}`
        );
    
    await logService.logOperation(logData);
    
    this.fetchActivities();
  };

  deleteActivity = async (id: string) => {
    const confirmed = window.confirm("Tem certeza que deseja excluir esta atividade?");
    if (!confirmed) return;

    // Busca o valor anterior para o log
    const dbRef = ref(db, `atividades/${id}`);
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }

    const deleteRef = ref(db, `atividades/${id}/deleted`);
    await set(deleteRef, true);
    
    // Registra o log
    if (oldValue) {
      const logData = logService.deleteLogData(
        'Activity',
        id,
        `atividades/${id}`,
        oldValue,
        `Atividade excluída: ${oldValue.title}`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchActivities();
  };
}

export const activityStore = new ActivityStore();
